/* eslint-disable @typescript-eslint/no-explicit-any */
import { NOT_APPLICABLE } from '@config/constants/common';
import { QueueStatus } from '@config/constants/enum';
import { ROLES } from '@config/constants/roles';
import { apiClient } from '@lib/api-client';
import { useAuthorization } from '@lib/authorization';
import { useQuery } from '@tanstack/react-query';
import { getCurrencyUnit } from '@utils/currency-units';
import {
	formatNumberWithSpaces,
	formatTwoDecimals,
} from '@utils/formats';
import {
	formatDate,
	formatQueryParams,
} from '@utils/functions';
import { getUnitLabel } from '@utils/units';
import Decimal from 'decimal.js';
import { getMeasurabilities } from 'shared/api/get-measurabilities';
import { ApiResponse } from '../interfaces';
import { TMesurabilityProject, TOffset } from '../types';

function checkIfNoInProgress(offsets: TOffset[]) {
	return !offsets.some(
		(obj: TOffset) => obj.status === 'IN_PROGRESS'
	);
}

export const getOffsets = (
	params: any
): Promise<ApiResponse> => {
	return apiClient.get('/offsets', {
		params: {
			page: params.page,
			pageSize: params.pageSize,
			query: formatQueryParams(params.queryParams),
			populate: [
				'organization',
				'organization.owner',
				'entity',
				'entity.contact',
				'tags',
				'certificates',
			],
			sort: params.sortParams,
		},
	});
};

export const getOffsetsWithoutContacts = (
	params: any
): Promise<ApiResponse> => {
	return apiClient.get('/offsets', {
		params: {
			page: params.page,
			pageSize: params.pageSize,
			query: formatQueryParams(params.queryParams),
			populate: [
				'organization',
				'entity',
				'tags',
				'certificates',
			],
			sort: params.sortParams,
		},
	});
};

export const useOffsets = (
	page: number,
	size: number,
	queryParams: any,
	sortParams: any
) => {
	const { checkRole, user } = useAuthorization();

	const isBrandUser = checkRole([
		ROLES.BRAND_ADMIN,
		ROLES.BRAND_STAFF,
	]);
	const isAdminUser = checkRole([
		ROLES.ADMIN,
		ROLES.SUPERADMIN,
	]);

	const isStaff = checkRole([ROLES.STAFF]);

	const brandEntities =
		Array.isArray(user?.roles) && isBrandUser
			? user.roles
					.filter(
						(r) =>
							r.role === ROLES.BRAND_ADMIN ||
							r.role === ROLES.BRAND_STAFF
					)
					.map((r) => r.entity)
			: null;

	const effectiveQueryParams = {
		...(isBrandUser && brandEntities
			? { ...queryParams, entity: brandEntities }
			: queryParams),
		...(isAdminUser
			? {}
			: { status: QueueStatus.SUCCESS }),
	};

	const offsetsQuery = useQuery({
		refetchInterval: (data) => {
			const rows = data?.state?.data?.rows || [];

			if (checkIfNoInProgress(rows)) {
				return false;
			}

			return 5000;
		},
		queryKey: [
			'offsets',
			{
				page: page,
				size: size,
				queryParams: queryParams,
				sortParams: sortParams,
			},
		],

		staleTime: 1000,
		queryFn: () => {
			if (!isAdminUser || isStaff) {
				return getOffsetsWithoutContacts({
					page: page,
					pageSize: size,
					queryParams: effectiveQueryParams,
					sortParams: sortParams,
				});
			}
			return getOffsets({
				page: page,
				pageSize: size,
				queryParams: effectiveQueryParams,
				sortParams: sortParams,
			});
		},
	});

	const mesurabilitiesQuery = useQuery({
		queryKey: ['mesurabilities'],
		queryFn: () => getMeasurabilities(),
	});

	const updateList = (
		offsets: any,
		mesurabilities: any
	) => {
		return offsets && Array.isArray(offsets)
			? offsets.reduce((acc: any, currentItem: any) => {
					const quantityOffset = new Decimal(
						currentItem.quantity_gco2eq
					);
					const quantity = Decimal.div(
						quantityOffset.toNumber(),
						1000000
					);

					const mesurabilitiesPerOffset = Array.isArray(
						mesurabilities
					)
						? mesurabilities
								.filter((item: TMesurabilityProject) =>
									Object.prototype.hasOwnProperty.call(
										currentItem.projectMixin_aco2,
										item.id
									)
								)
								.map((item: TMesurabilityProject) => {
									const quantityMesurability =
										new Decimal(
											currentItem?.projectMixin_aco2[
												item.id
											]
												? currentItem.projectMixin_aco2[
														item.id
													]
												: 0
										);

									const quantityMesurabilityTco2e =
										quantityMesurability.div(1000000);

									const price = new Decimal(
										item.sellPrice_europertco2eq
									);

									return {
										id: item.id,
										name: item.name,
										price: formatNumberWithSpaces(
											price.toNumber()
										),
										quantity: `${formatTwoDecimals(
											quantityMesurabilityTco2e.toNumber()
										)} ${getUnitLabel('tco2e')}`,
										total: formatNumberWithSpaces(
											quantityMesurabilityTco2e
												.mul(price)
												.toNumber()
										),
									};
								})
						: [];

					const modifiedItem = {
						...currentItem,
						brand: currentItem.entity
							? currentItem.entity.name
							: NOT_APPLICABLE,
						contributionDate: formatDate(
							currentItem.createdAt,
							'DD/MM/YYYY'
						),
						startDate: formatDate(
							currentItem.startDate,
							'DD/MM/YYYY'
						),
						endDate: formatDate(
							currentItem.endDate,
							'DD/MM/YYYY'
						),
						quantity: `${formatTwoDecimals(
							quantity.toNumber()
						)} ${getUnitLabel('tco2e')}`,

						mesurability: currentItem.projectMixin_aco2
							? Object.keys(currentItem.projectMixin_aco2)
									.length
							: 0,
						priceTTC:
							formatNumberWithSpaces(
								currentItem.amount_euro
							) + getCurrencyUnit('EUR'),
						mintStatus: currentItem.status
							? currentItem.status
							: NOT_APPLICABLE,
						mesurabilities: mesurabilitiesPerOffset,
						organizationData: currentItem?.organization
							? currentItem.organization
							: {},
						organization: currentItem.organization
							? currentItem.organization.name
							: NOT_APPLICABLE,

						meanPrice_europertco2eq:
							formatNumberWithSpaces(
								currentItem.meanPrice_europertco2eq
							) + getCurrencyUnit('EUR'),
						tags:
							currentItem &&
							currentItem.tags &&
							Array.isArray(currentItem.tags) &&
							currentItem.tags.length > 0
								? currentItem.tags
								: [],
						blockchain: currentItem?.blockchain
							? `${currentItem.blockchain
									.toLowerCase()
									.replace(/_/g, ' ')
									.replace(/^./, (char: string) =>
										char.toUpperCase()
									)}`
							: NOT_APPLICABLE,
					};
					acc.push(modifiedItem);

					return acc;
				}, [])
			: [];
	};

	return {
		data: offsetsQuery.data
			? updateList(
					offsetsQuery.data?.rows,
					mesurabilitiesQuery.data?.rows
				)
			: [],
		isLoading:
			offsetsQuery.isFetching && !offsetsQuery.isFetched,
		isFetched: offsetsQuery.isFetched,
		total: offsetsQuery.data?.total,
		page: offsetsQuery.data?.page,
		pageSize: offsetsQuery.data?.pageSize,
		totalPages: offsetsQuery.data?.totalPages,
	};
};
